import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Badge, Row, Col, Button, Form, ProgressBar, InputGroup } from 'react-bootstrap';

// Página pública: usa fetch() puro (sem o axios client autenticado), mas precisa da
// mesma base de URL configurada via REACT_APP_API_URL — sem isso, num ambiente onde o
// frontend e o backend vivem em domínios/CDNs diferentes (como o CloudFront na AWS),
// uma chamada relativa "/api/..." cai no próprio host do frontend em vez do backend.
const API_BASE = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

type TipoResposta = 'SIM_NAO' | 'PERCENTUAL' | 'TEXTO_LIVRE' | 'MULTIPLA_ESCOLHA';
type Etapa = 'carregando' | 'email' | 'codigo' | 'questionario';

interface IPergunta {
  qp_id: number;
  pergunta_text: string;
  tipo_resposta: TipoResposta;
  permite_na: boolean;
  nivel: 'principal' | 'complementar';
  ordem: number;
}

interface IAcesso {
  acesso_id: number;
  representante_nome: string;
  representante_email: string;
  status: string;
  data_alvo_resposta: string | null;
  questionario: { id: number; nome: string; descricao?: string; framework?: string; escopo?: string | null };
}

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const chaveSessao = (token: string) => `fourhands_acesso_sessao_${token}`;

const formatarContagem = (segundos: number): string => {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Entrada de resposta conforme o tipo da pergunta.
const AnswerInput: React.FC<{ p: IPergunta; value: any; onChange: (v: any) => void; disabled?: boolean }> = ({ p, value, onChange, disabled }) => {
  if (p.tipo_resposta === 'PERCENTUAL') {
    return (
      <div>
        <Form.Range min={0} max={100} value={value ?? 50} onChange={e => onChange(parseInt(e.target.value, 10))} disabled={disabled} />
        <span className="text-muted ms-2">{value ?? 50}%</span>
      </div>
    );
  }
  if (p.tipo_resposta === 'TEXTO_LIVRE') {
    return <Form.Control as="textarea" rows={2} value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled} placeholder="Digite sua resposta..." />;
  }
  // SIM_NAO (e demais) — botões
  const opts: { v: string; label: string; variant: string }[] = [
    { v: 'SIM', label: 'Sim', variant: 'success' },
    { v: 'NAO', label: 'Não', variant: 'danger' },
  ];
  if (p.permite_na) opts.push({ v: 'NA', label: 'N/A', variant: 'secondary' });
  return (
    <div className="d-flex gap-2 flex-wrap">
      {opts.map(o => (
        <Button
          key={o.v}
          size="sm"
          variant={value === o.v ? o.variant : `outline-${o.variant}`}
          onClick={() => !disabled && onChange(o.v)}
          disabled={disabled}
          style={{ minWidth: 64 }}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
};

// Página PÚBLICA (sem login): terceiro/fornecedor acessa e responde o questionário via
// link, mas antes precisa confirmar o e-mail e validar um código de acesso enviado a ele
// (2FA) — o token do link sozinho não basta mais para ver/responder o questionário.
const PublicAcesso: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [etapa, setEtapa] = useState<Etapa>('carregando');
  const [erro, setErro] = useState('');
  const [acesso, setAcesso] = useState<IAcesso | null>(null);
  const [sessao, setSessao] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [erroEmail, setErroEmail] = useState('');

  const [codigoInput, setCodigoInput] = useState('');
  const [codigoExpiraEm, setCodigoExpiraEm] = useState<string | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [validandoCodigo, setValidandoCodigo] = useState(false);
  const [erroCodigo, setErroCodigo] = useState('');

  const [perguntas, setPerguntas] = useState<IPergunta[]>([]);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [finalizado, setFinalizado] = useState(false);
  // Navegação uma pergunta por vez (em vez de listar tudo empilhado) — importante pra
  // questionários grandes (dezenas de perguntas) não virarem uma página gigante.
  const [indice, setIndice] = useState(0);

  const sessaoRef = useRef<string | null>(null);
  sessaoRef.current = sessao;

  // Busca as perguntas usando a sessão (pós-código). Se a sessão tiver expirado/for
  // inválida, volta pra tela de e-mail e limpa a sessão guardada.
  const carregarPerguntas = async (sessaoAtual: string) => {
    if (!token) return;
    const resp = await fetch(`${API_BASE}/acesso/${encodeURIComponent(token)}/perguntas`, {
      headers: { Authorization: `Bearer ${sessaoAtual}` },
    });
    if (resp.status === 401) {
      localStorage.removeItem(chaveSessao(token));
      setSessao(null);
      setEtapa('email');
      setErroEmail('Sua sessão expirou. Solicite um novo código.');
      return;
    }
    const json = await resp.json().catch(() => ({}));
    if (resp.ok) {
      setPerguntas(json.perguntas || []);
      setAnswers(json.respostas || {});
      setEtapa('questionario');
    }
  };

  // Validação inicial do link + tenta reaproveitar uma sessão já validada (guardada no
  // navegador) para este token específico, evitando pedir código de novo sem necessidade.
  useEffect(() => {
    if (!token) { setErro('Link inválido.'); return; }
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/acesso/${encodeURIComponent(token)}`);
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) { setErro(json.error || 'Link inválido ou expirado.'); return; }
        setAcesso(json as IAcesso);
        setEmail(json.representante_email || '');

        const sessaoGuardada = localStorage.getItem(chaveSessao(token));
        if (sessaoGuardada) {
          setSessao(sessaoGuardada);
          await carregarPerguntas(sessaoGuardada);
        } else {
          setEtapa('email');
        }
      } catch {
        setErro('Não foi possível validar o link agora. Tente novamente mais tarde.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Contagem regressiva de validade do código (2 min), pra habilitar "Receber novo código".
  useEffect(() => {
    if (etapa !== 'codigo' || !codigoExpiraEm) return;
    const tick = () => {
      const restante = Math.max(0, Math.floor((new Date(codigoExpiraEm).getTime() - Date.now()) / 1000));
      setSegundosRestantes(restante);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [etapa, codigoExpiraEm]);

  const solicitarCodigo = async () => {
    if (!token || !email.trim()) return;
    setEnviandoEmail(true);
    setErroEmail('');
    try {
      const resp = await fetch(`${API_BASE}/acesso/${encodeURIComponent(token)}/solicitar-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) { setErroEmail(json.error || 'Não foi possível enviar o código.'); return; }
      setCodigoExpiraEm(json.expira_em);
      setCodigoInput('');
      setErroCodigo('');
      setEtapa('codigo');
    } catch {
      setErroEmail('Erro de conexão. Tente novamente.');
    } finally {
      setEnviandoEmail(false);
    }
  };

  const validarCodigo = async () => {
    if (!token || codigoInput.trim().length !== 6) return;
    setValidandoCodigo(true);
    setErroCodigo('');
    try {
      const resp = await fetch(`${API_BASE}/acesso/${encodeURIComponent(token)}/validar-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo: codigoInput.trim() }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setErroCodigo(json.error || 'Código incorreto.');
        // Bloqueado após 3 tentativas: libera "Receber novo código" na hora, sem
        // esperar a contagem regressiva original do código anterior — zera a
        // expiração de referência pra o próximo tick do contador já dar 0.
        if (json.bloqueado) setCodigoExpiraEm(new Date(0).toISOString());
        return;
      }
      localStorage.setItem(chaveSessao(token), json.sessao);
      setSessao(json.sessao);
      setAcesso(prev => prev ? {
        ...prev,
        status: json.status,
        data_alvo_resposta: json.data_alvo_resposta,
        questionario: json.questionario || prev.questionario,
      } : prev);
      await carregarPerguntas(json.sessao);
    } catch {
      setErroCodigo('Erro de conexão. Tente novamente.');
    } finally {
      setValidandoCodigo(false);
    }
  };

  const setAnswer = (qpId: number, v: any) => setAnswers(prev => ({ ...prev, [qpId]: v }));

  const enviar = async (finalizar: boolean, opts?: { silencioso?: boolean }) => {
    if (!token || !sessaoRef.current) return false;
    setSaving(true);
    if (!opts?.silencioso) setMsg('');
    try {
      const payload = {
        finalizar,
        respostas: perguntas.map(p => ({
          qp_id: p.qp_id,
          resposta: p.tipo_resposta === 'PERCENTUAL' ? null : (answers[p.qp_id] ?? null),
          porcentagem: p.tipo_resposta === 'PERCENTUAL' ? (answers[p.qp_id] ?? null) : null,
        })),
      };
      const resp = await fetch(`${API_BASE}/acesso/${encodeURIComponent(token)}/respostas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessaoRef.current}` },
        body: JSON.stringify(payload),
      });
      if (resp.status === 401) {
        localStorage.removeItem(chaveSessao(token));
        setSessao(null);
        setEtapa('email');
        setErroEmail('Sua sessão expirou. Solicite um novo código.');
        return false;
      }
      const json = await resp.json().catch(() => ({}));
      if (resp.ok) {
        if (finalizar) {
          setFinalizado(true);
          setAcesso(prev => prev ? { ...prev, status: 'RESPONDIDO' } : prev);
        } else if (!opts?.silencioso) {
          setMsg('Progresso salvo.');
        }
        return true;
      }
      setMsg(json.error || 'Não foi possível salvar as respostas.');
      return false;
    } catch {
      setMsg('Erro de conexão ao salvar.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Avança salvando a resposta atual antes (autosave, igual ao comportamento do
  // questionário interno) — evita perder o progresso se o usuário fechar a aba no meio.
  const irProxima = async () => {
    if (indice >= perguntas.length - 1) return;
    const ok = await enviar(false, { silencioso: true });
    if (ok) setIndice(i => Math.min(perguntas.length - 1, i + 1));
  };

  const irAnterior = () => setIndice(i => Math.max(0, i - 1));

  const perguntaAtual = perguntas[indice];
  const progresso = perguntas.length > 0 ? Math.round(((indice + 1) / perguntas.length) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f3f5f8', padding: '32px 16px' }}>
      <Container style={{ maxWidth: 520 }}>
        <div className="text-center mb-4">
          <h1 style={{ color: '#1a3a5c', fontWeight: 700, letterSpacing: 1, marginBottom: 2 }}>FourHands LGPD</h1>
          <div className="text-muted" style={{ fontSize: 14 }}>Acesso ao Questionário</div>
        </div>

        {etapa === 'carregando' && !erro && (
          <Card body className="text-center py-5">
            <Spinner animation="border" /> <span className="ms-2">Validando seu acesso...</span>
          </Card>
        )}

        {erro && (
          <Alert variant="danger" className="text-center">
            <div style={{ fontSize: 40, lineHeight: 1 }}>🔒</div>
            <h5 className="mt-2 mb-1">Não foi possível acessar</h5>
            <div>{erro}</div>
            <div className="text-muted small mt-2">Verifique se copiou o link completo do e-mail.</div>
          </Alert>
        )}

        {!erro && etapa === 'email' && (
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Form.Group>
                <Form.Label className="fw-semibold">Usuário</Form.Label>
                <Form.Control
                  id="publicAcessoEmail"
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  isInvalid={!!erroEmail}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); solicitarCodigo(); } }}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">{erroEmail}</Form.Control.Feedback>
              </Form.Group>
              <Button className="w-100 mt-3" variant="primary" onClick={solicitarCodigo} disabled={enviandoEmail || !email.trim()}>
                {enviandoEmail ? <Spinner as="span" size="sm" animation="border" /> : 'Receber Código de Acesso'}
              </Button>
              <div className="text-muted small text-center mt-3">
                Você receberá um código para acesso ao sistema. Esse código expirará em
                2 minutos após o recebimento.
              </div>
            </Card.Body>
          </Card>
        )}

        {!erro && etapa === 'codigo' && (
          <Card className="shadow-sm">
            <Card.Body className="p-4 text-center">
              <h5 className="mb-1">Validação de Acesso</h5>
              <p className="text-muted small mb-3">
                Por favor, insira o código de 6 dígitos que enviamos para o seu e-mail.
              </p>
              <InputGroup className="mb-1" size="lg">
                <InputGroup.Text style={{ fontWeight: 600 }}>4Comply -</InputGroup.Text>
                <Form.Control
                  id="publicAcessoCodigo"
                  value={codigoInput}
                  onChange={e => setCodigoInput(e.target.value.toUpperCase().slice(0, 6))}
                  isInvalid={!!erroCodigo}
                  style={{ textTransform: 'uppercase', letterSpacing: 2 }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); validarCodigo(); } }}
                  autoFocus
                />
              </InputGroup>
              {erroCodigo && <div className="text-danger small mb-2">{erroCodigo}</div>}
              <Button className="w-100 mt-2" variant="primary" onClick={validarCodigo} disabled={validandoCodigo || codigoInput.trim().length !== 6}>
                {validandoCodigo ? <Spinner as="span" size="sm" animation="border" /> : 'Acessar o Sistema'}
              </Button>
              <div className="text-muted small mt-3">
                {segundosRestantes > 0 ? (
                  <>Não recebeu o código? Reenviar em {formatarContagem(segundosRestantes)}</>
                ) : (
                  <Button variant="link" size="sm" className="p-0" onClick={solicitarCodigo} disabled={enviandoEmail}>
                    {enviandoEmail ? 'Enviando...' : 'Receber novo código'}
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {!erro && etapa === 'questionario' && acesso && (
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <Card className="shadow-sm mb-3">
              <Card.Header style={{ background: '#1a3a5c', color: '#fff' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <span className="fw-semibold">{acesso.questionario.nome}</span>
                  <Badge bg="light" text="dark">{acesso.status}</Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-2">
                  Olá{acesso.representante_nome ? `, ${acesso.representante_nome}` : ''}! Responda as perguntas
                  abaixo. Você pode salvar o progresso e voltar a este mesmo link quando quiser atualizar.
                </p>
                <Row className="g-3 mt-1">
                  <Col sm={4}><div className="text-muted small text-uppercase">Framework</div><div>{acesso.questionario.framework || '—'}</div></Col>
                  <Col sm={4}><div className="text-muted small text-uppercase">Escopo</div><div>{acesso.questionario.escopo || '—'}</div></Col>
                  <Col sm={4}><div className="text-muted small text-uppercase">Prazo alvo</div><div>{formatarData(acesso.data_alvo_resposta)}</div></Col>
                </Row>
              </Card.Body>
            </Card>

            {finalizado ? (
              <Alert variant="success" className="text-center py-4">
                <div style={{ fontSize: 40, lineHeight: 1 }}>✅</div>
                <h5 className="mt-2 mb-1">Respostas enviadas com sucesso!</h5>
                <div>Obrigado, {acesso.representante_nome || 'representante'}. Recebemos suas respostas.</div>
              </Alert>
            ) : perguntas.length === 0 ? (
              <Alert variant="info">Este questionário ainda não possui perguntas configuradas.</Alert>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">Pergunta {indice + 1} de {perguntas.length}</span>
                  <span className="text-muted small">{progresso}%</span>
                </div>
                <ProgressBar now={progresso} variant="primary" style={{ height: 8 }} className="mb-3" />

                <Card className="shadow-sm">
                  <Card.Body>
                    {perguntaAtual && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Badge bg={perguntaAtual.nivel === 'principal' ? 'primary' : 'secondary'}>
                            {perguntaAtual.nivel === 'principal' ? `Pergunta ${indice + 1}` : 'Complementar'}
                          </Badge>
                        </div>
                        <div className="mb-3" style={{ fontWeight: 500, fontSize: '1.05rem' }}>{perguntaAtual.pergunta_text}</div>
                        <AnswerInput
                          p={perguntaAtual}
                          value={answers[perguntaAtual.qp_id]}
                          onChange={v => setAnswer(perguntaAtual.qp_id, v)}
                        />
                      </div>
                    )}

                    {msg && <Alert variant={msg.includes('salvo') ? 'success' : 'danger'} className="mt-3 mb-0">{msg}</Alert>}

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <Button variant="outline-secondary" onClick={irAnterior} disabled={saving || indice === 0}>
                        ← Anterior
                      </Button>
                      <div className="d-flex gap-2">
                        <Button variant="outline-secondary" onClick={() => enviar(false)} disabled={saving}>
                          {saving ? <Spinner as="span" size="sm" animation="border" /> : 'Salvar progresso'}
                        </Button>
                        {indice < perguntas.length - 1 ? (
                          <Button variant="primary" onClick={irProxima} disabled={saving}>
                            {saving ? <Spinner as="span" size="sm" animation="border" /> : 'Próxima →'}
                          </Button>
                        ) : (
                          <Button variant="primary" onClick={() => enviar(true)} disabled={saving}>
                            {saving ? <Spinner as="span" size="sm" animation="border" /> : 'Enviar respostas'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </>
            )}
          </div>
        )}

        <div className="text-center text-muted mt-4" style={{ fontSize: 12 }}>© {new Date().getFullYear()} FourHands LGPD</div>
      </Container>
    </div>
  );
};

export default PublicAcesso;
